import { z } from "zod";
import { ConfidenceSchema, IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, PositiveIntegerSchema, SourceRefSchema, UnknownRecordSchema } from "./source.js";
import { ClassificationArtifactRowSchema, RequirementRegisterArtifactSchema, RequirementRegisterRowSchema, RequirementUnresolvedRegisterRowSchema, ManifestDocumentRowSchema, TenderDocumentRegistryRowSchema, TenderObjectRefSchema, TenderPackCountsSchema, TenderTakeoffArtifactSchema, TenderTakeoffAdapterRowSchema, TenderStageSchema, TenderUploadStateSchema } from "./tenderIntake.js";
export const TENDER_DOCUMENT_WORKSPACE_SCHEMA_VERSION = "tender-document-workspace.v1";
export const TenderEngineArtifactRefSchema = z
    .object({
    path: NonEmptyStringSchema,
    sha256: NonEmptyStringSchema,
    objectRef: TenderObjectRefSchema.optional(),
    durableUploadError: NonEmptyStringSchema.optional()
})
    .strict();
export const TenderEngineClassifyProvenanceSchema = z
    .object({
    runId: IdSchema,
    stageId: z.literal("classify"),
    artifactRefs: z.array(TenderEngineArtifactRefSchema)
})
    .strict();
/**
 * The classify engine is the source of truth for this part of the workspace.
 * Known envelope fields are checked, while passthrough preserves new engine
 * fields instead of silently reducing the run artifact to a legacy shape.
 */
export const TenderEngineClassifyDocumentSchema = z
    .object({
    document_id: IdSchema,
    path: NonEmptyStringSchema,
    applicable_types: z.array(NonEmptyStringSchema),
    registry_discipline: z.unknown(),
    status: NonEmptyStringSchema,
    inspection_kind: NonEmptyStringSchema,
    metadata: UnknownRecordSchema,
    route_decision: UnknownRecordSchema,
    artifact_refs: z.array(z.unknown()),
    reason: NonEmptyStringSchema.optional(),
    result: UnknownRecordSchema.optional(),
    route_records: z.array(UnknownRecordSchema).optional(),
    predictions: z.array(UnknownRecordSchema).optional(),
    provenance: TenderEngineClassifyProvenanceSchema
})
    .passthrough();
export const TenderDocumentEstimatorStateFamilySchema = z.enum([
    "needs_review",
    "failed",
    "extracting",
    "unprocessed",
    "terminal"
]);
export const TenderDocumentTerminalSubstatusSchema = z.enum([
    "parsed",
    "excluded",
    "unsupported",
    "no_result"
]);
export const TenderDocumentStateReasonCodeSchema = z.enum([
    "manifest_failed",
    "classification_failed",
    "parse_failed",
    "progress_failed",
    "extracting",
    "classification_unsure",
    "classification_review_required",
    "parse_blocked",
    "parse_partial",
    "verification_flag",
    "requirement_crawl_blocked",
    "requirement_provenance_unresolved",
    "takeoff_blocker",
    "takeoff_warning",
    "reconciliation_conflict",
    "reviewer_flag",
    "generated_decision_review",
    "human_review_blocked",
    "replay_requested",
    "output_unavailable",
    "awaiting_manifest",
    "awaiting_classification",
    "awaiting_parse",
    "classification_no_result",
    "parsed",
    "excluded",
    "unsupported"
]);
export const TenderDocumentSearchTargetKindSchema = z.enum([
    "document",
    "apartment_row",
    "apartment_warning",
    "requirement",
    "requirement_unresolved",
    "parsed_table",
    "text_span",
    "drawing_field",
    "drawing_disagreement",
    "drawing_decision",
    "drawing_reviewer_flag"
]);
export const TenderDocumentSearchTargetSchema = z
    .object({
    hitKey: NonEmptyStringSchema,
    kind: z.enum(["metadata", "extracted_content"]),
    targetKind: TenderDocumentSearchTargetKindSchema.optional(),
    field: NonEmptyStringSchema,
    text: NonEmptyStringSchema,
    targetId: IdSchema.optional(),
    artifactRevisionId: IdSchema.optional(),
    evidenceKey: NonEmptyStringSchema.optional()
})
    .strict();
export const TenderDocumentWorkspaceParseSummarySchema = z
    .object({
    artifactId: IdSchema,
    artifactRevisionId: IdSchema,
    parseRoute: z.enum([
        "native-document-agent",
        "vision-render-agent",
        "spreadsheet-tool-plus-agent",
        "email-tool-plus-agent",
        "unsupported"
    ]),
    parseCompleteness: z.enum(["complete", "partial", "blocked", "waived"]),
    verificationFlags: z.array(NonEmptyStringSchema),
    updatedAt: IsoTimestampSchema
})
    .strict();
export const TenderDocumentWorkspaceProgressSummarySchema = z
    .object({
    eventId: IdSchema,
    eventType: NonEmptyStringSchema,
    stage: TenderStageSchema,
    stageStatus: NonEmptyStringSchema,
    message: NonEmptyStringSchema,
    createdAt: IsoTimestampSchema,
    elapsedMs: NonNegativeIntegerSchema.optional()
})
    .strict();
export const TenderDocumentWorkspaceFailureSummarySchema = z
    .object({
    stage: TenderStageSchema,
    code: NonEmptyStringSchema.optional(),
    message: NonEmptyStringSchema.optional(),
    retryable: z.boolean().optional(),
    updatedAt: IsoTimestampSchema.optional()
})
    .strict();
export const TenderDocumentWorkspaceStateSchema = z
    .object({
    family: TenderDocumentEstimatorStateFamilySchema,
    terminalSubstatus: TenderDocumentTerminalSubstatusSchema.optional(),
    reasonCodes: z.array(TenderDocumentStateReasonCodeSchema).min(1),
    headline: NonEmptyStringSchema,
    isTerminal: z.boolean(),
    failedStage: TenderStageSchema.optional()
})
    .strict();
export const TenderDocumentWorkspaceSummaryRowSchema = z
    .object({
    docId: IdSchema,
    packId: IdSchema,
    batchOrdinal: PositiveIntegerSchema,
    registry: TenderDocumentRegistryRowSchema,
    manifest: ManifestDocumentRowSchema.nullable(),
    classification: ClassificationArtifactRowSchema.nullable(),
    engineClassification: TenderEngineClassifyDocumentSchema.optional(),
    parse: TenderDocumentWorkspaceParseSummarySchema.nullable(),
    summary: z
        .object({
        apartmentTypeCount: NonNegativeIntegerSchema,
        requirementCount: NonNegativeIntegerSchema,
        provenanceUnresolvedRequirementCount: NonNegativeIntegerSchema.default(0),
        conflictCount: NonNegativeIntegerSchema,
        warningCount: NonNegativeIntegerSchema,
        blockerCount: NonNegativeIntegerSchema,
        classificationConfidence: ConfidenceSchema.nullable()
    })
        .strict(),
    latestProgress: TenderDocumentWorkspaceProgressSummarySchema.nullable(),
    failure: TenderDocumentWorkspaceFailureSummarySchema.nullable(),
    state: TenderDocumentWorkspaceStateSchema,
    searchTargets: z.array(TenderDocumentSearchTargetSchema)
})
    .strict();
export const TenderDocumentWorkspaceTombstoneSchema = z
    .object({
    docId: IdSchema,
    registry: TenderDocumentRegistryRowSchema,
    deletedAt: IsoTimestampSchema.nullable(),
    reason: z.enum(["deleting", "object_deleted", "metadata_deleted"]),
    classification: ClassificationArtifactRowSchema.nullable().optional(),
    state: TenderDocumentWorkspaceStateSchema.optional(),
    searchTargets: z.array(TenderDocumentSearchTargetSchema).optional()
})
    .strict();
export const TenderDocumentWorkspaceSearchEntrySchema = z
    .object({
    docId: IdSchema,
    targets: z.array(TenderDocumentSearchTargetSchema)
})
    .strict();
export const TenderDocumentWorkspacePackSchema = z
    .object({
    packId: IdSchema,
    batchOrdinal: PositiveIntegerSchema,
    packName: NonEmptyStringSchema,
    createdAt: IsoTimestampSchema,
    uploadState: TenderUploadStateSchema,
    counts: TenderPackCountsSchema,
    artifactRevisionId: IdSchema
})
    .strict();
export const TenderDocumentWorkspaceResponseSchema = z
    .object({
    schemaVersion: z.literal(TENDER_DOCUMENT_WORKSPACE_SCHEMA_VERSION),
    projectId: IdSchema,
    pack: z
        .object({
        packId: IdSchema,
        tenderId: IdSchema,
        artifactRevisionId: IdSchema,
        updatedAt: IsoTimestampSchema
    })
        .strict()
        .nullable(),
    packs: z.array(TenderDocumentWorkspacePackSchema),
    rows: z.array(TenderDocumentWorkspaceSummaryRowSchema),
    tombstones: z.array(TenderDocumentWorkspaceTombstoneSchema),
    counts: z
        .object({
        total: NonNegativeIntegerSchema,
        terminal: NonNegativeIntegerSchema,
        extracted: NonNegativeIntegerSchema,
        needsReview: NonNegativeIntegerSchema,
        failed: NonNegativeIntegerSchema,
        extracting: NonNegativeIntegerSchema,
        unprocessed: NonNegativeIntegerSchema,
        deleting: NonNegativeIntegerSchema
    })
        .strict(),
    searchEntries: z.array(TenderDocumentWorkspaceSearchEntrySchema),
    fetchedAt: IsoTimestampSchema,
    revisionToken: NonEmptyStringSchema
})
    .strict();
export const TENDER_DOCUMENT_DETAIL_SCHEMA_VERSION = "tender-document-detail.v1";
export const TENDER_TAKEOFF_REVIEW_SCHEMA_VERSION = "tender-takeoff-review.v1";
export const TenderTakeoffReviewTargetKindSchema = z.enum([
    "apartment_fact",
    "drawing_field"
]);
export const TenderTakeoffReviewOutcomeSchema = z.enum([
    "verified_unchanged",
    "accepted_source",
    "revised",
    "blocked",
    "replay_requested"
]);
export const TenderTakeoffReviewEventSchema = z
    .object({
    schemaVersion: z.literal(TENDER_TAKEOFF_REVIEW_SCHEMA_VERSION),
    eventId: IdSchema,
    orgId: IdSchema,
    projectId: IdSchema,
    tenderId: IdSchema,
    packId: IdSchema,
    docId: IdSchema,
    targetKind: TenderTakeoffReviewTargetKindSchema,
    artifactId: IdSchema,
    artifactRevisionId: IdSchema,
    rowId: IdSchema.optional(),
    field: NonEmptyStringSchema,
    outcome: TenderTakeoffReviewOutcomeSchema,
    reviewedBy: z
        .object({
        userId: IdSchema,
        displayLabel: NonEmptyStringSchema
    })
        .strict(),
    reviewedAt: IsoTimestampSchema,
    reason: NonEmptyStringSchema,
    beforeValue: z.unknown(),
    resolvedValue: z.unknown().optional(),
    sourceRefs: z.array(SourceRefSchema),
    expectedArtifactRevisionId: IdSchema,
    idempotencyKey: NonEmptyStringSchema,
    requestFingerprint: NonEmptyStringSchema,
    invalidatedStages: z.array(TenderStageSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    if (!Object.prototype.hasOwnProperty.call(value, "beforeValue")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "take-off review events require the generated value being reviewed",
            path: ["beforeValue"]
        });
    }
    if (value.artifactRevisionId !== value.expectedArtifactRevisionId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "review event artifact revision must match its expected revision",
            path: ["expectedArtifactRevisionId"]
        });
    }
    if (value.targetKind === "apartment_fact" && !value.rowId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "apartment fact review events require a row id",
            path: ["rowId"]
        });
    }
    if (value.targetKind === "apartment_fact" && value.outcome === "accepted_source") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "accepted_source is only valid for drawing fields",
            path: ["outcome"]
        });
    }
    if (value.outcome === "revised" &&
        !Object.prototype.hasOwnProperty.call(value, "resolvedValue")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "revised review events require a resolved value",
            path: ["resolvedValue"]
        });
    }
});
export const TenderTakeoffReviewResponseSchema = z
    .object({
    ok: z.literal(true),
    event: TenderTakeoffReviewEventSchema,
    currentArtifactRevisionId: IdSchema,
    projectedRemainingReviewReasons: z.array(NonEmptyStringSchema),
    invalidatedStages: z.array(TenderStageSchema),
    duplicate: z.boolean()
})
    .strict();
export const TenderDocumentScheduleDetailOutcomeSchema = z.enum([
    "rows",
    "valid_empty",
    "in_progress",
    "missing_typed_output",
    "failed"
]);
export const TenderDocumentScheduleAdapterArtifactSchema = z
    .object({
    artifactId: IdSchema,
    artifactRevisionId: IdSchema,
    adapterVersion: NonEmptyStringSchema,
    updatedAt: IsoTimestampSchema,
    rows: z.array(TenderTakeoffAdapterRowSchema)
})
    .strict();
export const TenderDocumentScheduleDetailSchema = z
    .object({
    kind: z.literal("schedule"),
    outcome: TenderDocumentScheduleDetailOutcomeSchema,
    artifacts: z.array(TenderDocumentScheduleAdapterArtifactSchema),
    reviewEvents: z.array(TenderTakeoffReviewEventSchema).default([])
})
    .strict()
    .superRefine((value, ctx) => {
    const rowCount = value.artifacts.reduce((total, artifact) => total + artifact.rows.length, 0);
    if (value.outcome === "rows" && rowCount === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "schedule rows outcome requires at least one validated adapter row",
            path: ["artifacts"]
        });
    }
    if (value.outcome === "valid_empty" &&
        (value.artifacts.length === 0 || rowCount !== 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "schedule valid_empty outcome requires a matched adapter artifact with zero rows",
            path: ["artifacts"]
        });
    }
});
export const TenderDocumentExtractedContentOutcomeSchema = z.enum([
    "content",
    "in_progress",
    "missing_output",
    "failed"
]);
export const TenderDocumentParsedTableSchema = z
    .object({
    tableId: IdSchema,
    page: z.number().int().positive().optional(),
    sheetName: NonEmptyStringSchema.optional(),
    rowsObjectRef: TenderObjectRefSchema.optional(),
    sourceRef: SourceRefSchema,
    confidence: ConfidenceSchema,
    evidenceKey: NonEmptyStringSchema
})
    .strict();
export const TenderDocumentParsedTextSpanSchema = z
    .object({
    spanId: IdSchema,
    page: z.number().int().positive().optional(),
    sheetName: NonEmptyStringSchema.optional(),
    cellRange: NonEmptyStringSchema.optional(),
    text: NonEmptyStringSchema,
    sourceRef: SourceRefSchema,
    confidence: ConfidenceSchema,
    evidenceKey: NonEmptyStringSchema
})
    .strict();
export const TenderDocumentParseProjectionSchema = z
    .object({
    artifactId: IdSchema,
    artifactRevisionId: IdSchema,
    updatedAt: IsoTimestampSchema,
    parseCompleteness: z.enum(["complete", "partial", "blocked", "waived"]),
    verificationFlags: z.array(NonEmptyStringSchema),
    tables: z.array(TenderDocumentParsedTableSchema),
    textSpans: z.array(TenderDocumentParsedTextSpanSchema)
})
    .strict();
export const TenderDocumentRequirementRowSchema = RequirementRegisterRowSchema.innerType()
    .extend({
    evidenceKey: NonEmptyStringSchema
})
    .strict()
    .superRefine((value, ctx) => {
    const { evidenceKey: _evidenceKey, ...requirementRow } = value;
    const parsed = RequirementRegisterRowSchema.safeParse(requirementRow);
    if (!parsed.success) {
        parsed.error.issues.forEach((issue) => ctx.addIssue({
            ...issue,
            path: issue.path
        }));
    }
});
export const TenderDocumentUnresolvedRequirementRowSchema = RequirementUnresolvedRegisterRowSchema;
export const TenderDocumentSpecificationDetailSchema = z
    .object({
    kind: z.literal("specification"),
    outcome: TenderDocumentExtractedContentOutcomeSchema,
    parseArtifact: TenderDocumentParseProjectionSchema.nullable(),
    requirementArtifactRevisionId: IdSchema.nullable(),
    requirementArtifactUpdatedAt: IsoTimestampSchema.nullable(),
    requirements: z.array(TenderDocumentRequirementRowSchema),
    unresolvedRequirements: z
        .array(TenderDocumentUnresolvedRequirementRowSchema)
        .default([])
})
    .strict();
export const TenderDocumentTextDetailSchema = z
    .object({
    kind: z.literal("text"),
    docType: z.enum([
        "scope",
        "correspondence",
        "commercial",
        "information",
        "unrelated",
        "unsupported"
    ]),
    outcome: TenderDocumentExtractedContentOutcomeSchema,
    parseArtifact: TenderDocumentParseProjectionSchema.nullable(),
    requirementArtifactRevisionId: IdSchema.nullable(),
    requirementArtifactUpdatedAt: IsoTimestampSchema.nullable(),
    requirements: z.array(TenderDocumentRequirementRowSchema),
    unresolvedRequirements: z
        .array(TenderDocumentUnresolvedRequirementRowSchema)
        .default([])
})
    .strict();
export const TenderDocumentDrawingRenderObjectSchema = z
    .object({
    renderObjectId: IdSchema,
    docId: IdSchema,
    page: z.number().int().positive(),
    tileId: IdSchema.optional(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    region: SourceRefSchema.shape.region
})
    .strict();
export const TenderDocumentDrawingArtifactSchema = z
    .object({
    artifact: TenderTakeoffArtifactSchema,
    fieldEvidenceKeys: z.record(z.string(), NonEmptyStringSchema)
})
    .strict();
export const TenderDocumentDrawingScheduleRowSchema = z
    .object({
    artifactId: IdSchema,
    artifactRevisionId: IdSchema,
    sourceDocId: IdSchema,
    row: TenderTakeoffAdapterRowSchema
})
    .strict()
    .superRefine((value, ctx) => {
    if (!value.row.apartmentTakeoffRow.sourceRefs.some((sourceRef) => sourceRef.sourceId === value.sourceDocId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing schedule comparison rows require schedule provenance",
            path: ["row", "apartmentTakeoffRow", "sourceRefs"]
        });
    }
});
export const TenderDocumentDrawingDetailSchema = z
    .object({
    kind: z.literal("drawing"),
    outcome: TenderDocumentExtractedContentOutcomeSchema,
    pageCount: NonNegativeIntegerSchema.nullable(),
    parseArtifactRevisionId: IdSchema.nullable(),
    parseArtifactUpdatedAt: IsoTimestampSchema.nullable(),
    renderObjects: z.array(TenderDocumentDrawingRenderObjectSchema),
    artifacts: z.array(TenderDocumentDrawingArtifactSchema),
    scheduleRows: z.array(TenderDocumentDrawingScheduleRowSchema),
    reviewEvents: z.array(TenderTakeoffReviewEventSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.outcome === "content" &&
        value.artifacts.length === 0 &&
        value.renderObjects.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing content outcome requires a current take-off artifact or persisted render object",
            path: ["renderObjects"]
        });
    }
});
export const TenderDocumentEvidenceHistoryEntrySchema = z
    .object({
    evidenceKey: NonEmptyStringSchema,
    artifactRevisionId: IdSchema,
    artifactUpdatedAt: IsoTimestampSchema,
    current: z.boolean()
})
    .strict();
export const TenderDocumentDetailResponseSchema = z
    .object({
    schemaVersion: z.literal(TENDER_DOCUMENT_DETAIL_SCHEMA_VERSION),
    projectId: IdSchema,
    pack: z
        .object({
        packId: IdSchema,
        tenderId: IdSchema,
        artifactRevisionId: IdSchema,
        updatedAt: IsoTimestampSchema
    })
        .strict(),
    document: TenderDocumentWorkspaceSummaryRowSchema,
    detail: z.union([
        TenderDocumentScheduleDetailSchema,
        TenderDocumentSpecificationDetailSchema,
        TenderDocumentTextDetailSchema,
        TenderDocumentDrawingDetailSchema
    ]),
    evidenceHistory: z.array(TenderDocumentEvidenceHistoryEntrySchema).optional(),
    fetchedAt: IsoTimestampSchema,
    revisionToken: NonEmptyStringSchema
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.detail.kind === "specification" || value.detail.kind === "text") {
        for (const [rowIndex, row] of value.detail.requirements.entries()) {
            if (row.sourceDocId !== value.document.docId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "document requirement rows require sourceDocId for the selected document",
                    path: ["detail", "requirements", rowIndex, "sourceDocId"]
                });
            }
        }
        for (const [rowIndex, row] of value.detail.unresolvedRequirements.entries()) {
            if (row.sourceDocId !== value.document.docId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "document unresolved requirement rows require sourceDocId for the selected document",
                    path: ["detail", "unresolvedRequirements", rowIndex, "sourceDocId"]
                });
            }
        }
        return;
    }
    if (value.detail.kind !== "schedule") {
        return;
    }
    for (const [artifactIndex, artifact] of value.detail.artifacts.entries()) {
        for (const [rowIndex, row] of artifact.rows.entries()) {
            if (!row.apartmentTakeoffRow.sourceRefs.some((sourceRef) => sourceRef.sourceId === value.document.docId)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "schedule detail rows require apartment row provenance for the selected document",
                    path: [
                        "detail",
                        "artifacts",
                        artifactIndex,
                        "rows",
                        rowIndex,
                        "apartmentTakeoffRow",
                        "sourceRefs"
                    ]
                });
            }
        }
    }
});
export const TENDER_SOURCE_VIEW_SCHEMA_VERSION = "tender-source-view.v1";
export const TenderSourceViewResponseSchema = z
    .object({
    schemaVersion: z.literal(TENDER_SOURCE_VIEW_SCHEMA_VERSION),
    mode: z.enum(["authenticated_stream", "signed_url"]),
    viewUrl: NonEmptyStringSchema,
    downloadUrl: NonEmptyStringSchema,
    expiresAt: IsoTimestampSchema.nullable(),
    contentType: NonEmptyStringSchema,
    precision: z.enum(["exact", "broader"]),
    precisionLabel: NonEmptyStringSchema,
    fallbackLabel: NonEmptyStringSchema,
    scope: z
        .object({
        orgId: IdSchema,
        projectId: IdSchema,
        packId: IdSchema,
        docId: IdSchema,
        objectGeneration: NonEmptyStringSchema
    })
        .strict()
})
    .strict();
export const TenderRequirementRowsReviewResponseSchema = z
    .object({
    ok: z.literal(true),
    requirementRegisterArtifact: RequirementRegisterArtifactSchema,
    engineFeedRequirements: RequirementRegisterArtifactSchema.shape.engineFeedRows,
    currentArtifactRevisionId: IdSchema,
    idempotencyKey: NonEmptyStringSchema,
    duplicate: z.boolean()
})
    .strict();
//# sourceMappingURL=tenderWorkspace.js.map